/**
 * Content Ideas library — Learn-Mode inline tips (lane B6).
 *
 * Anchored across `routes/social/ContentIdeas.tsx` and
 * `components/content-ideas/IdeaDrawer.tsx`.
 */

import type { LearnTip } from "./types";

export const contentIdeasTips: LearnTip[] = [
  {
    key: "social-ideas",
    title: "Content ideas",
    body: "A library of short-video ideas that are working right now, each with a shot-by-shot storyboard. Pick a client at the top to see the ideas that fit their industry.",
  },
  {
    key: "social-ideas-filters",
    title: "Filter the library",
    body: "Narrow ideas by industry, channel, type, trend, effort, humor style or hook, or pick a format, pillar or minimum score. Search looks at the title, the hook and the format.",
    bullets: [
      "Rising: picking up fast. Peaking: at its most popular now. Evergreen: works any time.",
      "Effort is how hard the video is to shoot.",
    ],
  },
  {
    key: "social-ideas-sort",
    title: "Sort the library",
    body: "Trending puts rising ideas first. Top scored puts the ideas with the highest score first.",
    bullets: [
      "Score is out of 100: impact, confidence and ease multiplied together.",
      "Min score hides the weaker ideas.",
    ],
  },
  {
    key: "social-ideas-tabs",
    title: "Library, What's working and Trend radar",
    body: "Switch between the idea library, a view of what is already working for the selected client, and the trend radar.",
  },
  {
    key: "social-ideas-grid",
    title: "Ideas",
    body: "Rising ideas come first, then the ones used most. Click a card to see the full storyboard.",
  },
  {
    key: "social-ideas-detail",
    title: "Storyboard",
    body: "The shot list gives each shot's time in the reel, what to film, the on-screen text and the audio. Why we think it works shows the score and the posts it is based on. Check the compliance note before you film.",
  },
  {
    key: "social-ideas-reference",
    title: "Reference image",
    body: "The trending image or clip this idea is based on. Click it to see it full size, then match the framing, expression and lighting in your own shot.",
    bullets: ["It is someone else's content. Use it as a guide only and never post the file."],
  },
  {
    key: "social-ideas-actions",
    title: "Use this idea",
    body: "Write it now opens a new post with the hook and call to action filled in. Add to calendar saves a draft brief on the date you pick so the shoot gets planned.",
    bullets: ["Neither option schedules or publishes anything."],
  },
  {
    key: "social-insights",
    title: "What's working",
    body: "How the selected client's own posts have performed, so you can lean on the formats and hooks that already work. Pick a client at the top first.",
  },
  {
    key: "social-insights-summary",
    title: "The headline numbers",
    body: "Posts evaluated is how many posts were compared. Humor lift is how much better humor posts do than the rest. Top format is the best-performing format with enough posts to trust.",
  },
  {
    key: "social-insights-humor",
    title: "Humor vs everything else",
    body: "Compares the client's humor posts with their other posts. A bar past the thin line did better than a typical post.",
  },
  {
    key: "social-insights-charts",
    title: "Performance bars",
    body: "Each bar is the median performance index for a group of posts. x1 is a typical post for its media type, x2 is twice as good.",
    bullets: [
      "Greyed groups marked low sample have too few posts to trust.",
      "Posts under 14 days old are left out while they gather views.",
    ],
  },
  {
    key: "social-insights-posts",
    title: "Top posts",
    body: "The client's best posts by performance index, each with its views and format. Open the post to see it on the platform.",
  },
  {
    key: "social-insights-coverage",
    title: "Humor & trends coverage",
    body: "Where humor, audio and GIPHY are used. Pick a client for their full breakdown, or leave it on all clients for one row each.",
    bullets: [
      "Performance (pi) is performance vs the brand's typical post: x1 is typical, x2 is twice as good.",
      "Low sample means too few posts to act on yet.",
      "Queue is the posts already scheduled ahead.",
    ],
  },
  {
    key: "social-trends",
    title: "Trend radar",
    body: "The log for the trend fast lane (SM-062). Each trend shows how early it is, how long the window to post is open, and whether it passed the 4-point screen. A trend post still needs a person to approve it, one post at a time.",
    bullets: ["When a window has closed, don't post."],
  },
  {
    key: "social-trends-filters",
    title: "Filter the radar",
    body: "The live board shows trends being watched and approved formats. Use the status pills to see used, skipped or expired ones, and narrow by kind, stage or only trends whose window is still open.",
    bullets: ["Pick a client at the top to see their trends plus the ones that apply to every client."],
  },
  {
    key: "social-trends-list",
    title: "Trends",
    body: "Soonest-closing window first. The chips show the 4-point screen: Stage, Fit, Rights and Safety, each pass, fail or not assessed. Open Screen notes to read why.",
    bullets: [
      "Emerging and growing trends are worth a look. Mainstream, saturated and dead ones are usually a skip.",
      "Review & edit opens the trend to update the screen or window.",
    ],
  },
  {
    key: "social-trends-detail",
    title: "Review a trend",
    body: "Set the stage, mark each screen point and set when the window closes. Mark as approved format unlocks once all four points pass. Skip needs a reason, which is saved with the trend.",
    bullets: ["Approving a format does not post anything."],
  },
  {
    key: "social-trends-new",
    title: "Add a trend",
    body: "Log a trend you spotted. Memes, audio, challenges and formats get a 48-hour window from the time you first saw them.",
  },
  {
    key: "social-trends-audio",
    title: "Rank change in Meta's music library",
    body: "How far each sound moved up or down Meta's business music library between our daily snapshots. This is not a trend chart: Instagram does not publish one.",
    bullets: ["Needs at least two daily snapshots before anything shows."],
  },
];
